import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import {
  MinOptionInfo,
  SingleSelectBase,
  Stack,
  type StackProps,
} from '@stitch-fix/mode-react';
import type { SelectableProps } from '../OnboardingNavigation/MultiLineSelectable/MultiLineSelectable';

type StringMinOptionInfo<TFieldValues extends FieldValues> = MinOptionInfo<
  PathValue<TFieldValues, Path<TFieldValues>>
>;

interface ControlledSingleSelectProps<
  TOption extends StringMinOptionInfo<TFieldValues>,
  TFieldValues extends FieldValues,
> {
  /**
   * The `control` object returned by `useForm`.
   */
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * A key name configured in the corresponding zod schema, i.e.,
   * pass `"selections"` if the zod schema is `z.object({ selections: ... })`.
   */
  name: UseControllerProps<TFieldValues>['name'];
  /**
   * An array of options. Each object must contain a `value` field.
   */
  options: TOption[];
  /**
   * Enables using a custom UI component to render each of the options.
   * The render prop's params include the option fields and an `isSelected` field,
   * which indicates whether or not the given option is currently selected.
   */
  optionRender: (props: TOption & SelectableProps) => React.ReactElement;
  /**
   * The spacing between each option in the stack.
   */
  spacing?: StackProps['spacing'];
}

/**
 * This component allows [React Hook Form](https://react-hook-form.com/)
 * to easily communicate with Mode React's [SingleSelectBase](https://mode-react.daylight.stitchfix.com/?path=/docs/base-components-single-select-base--example)
 */
export const ControlledSingleSelect = <
  TOption extends StringMinOptionInfo<TFieldValues>,
  TFieldValues extends FieldValues,
>({
  control,
  name,
  options,
  optionRender,
  spacing = 1,
}: ControlledSingleSelectProps<TOption, TFieldValues>) => {
  const {
    field: { onChange, value },
  } = useController({ control, name });

  return (
    <SingleSelectBase
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      container={children => <Stack spacing={spacing}>{children}</Stack>}
    >
      {(selectableProps, currentOption) =>
        optionRender({
          ...selectableProps,
          ...currentOption,
        })
      }
    </SingleSelectBase>
  );
};
